import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Container,
  CircularProgress,
  IconButton,
  Paper,
  Button,
  Portal,
  Box,
} from "@mui/material";
import { Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import CreateIcon from "@mui/icons-material/Create";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import PlaylistContentManager from "./PlaylistContentManager";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import TheatersIcon from "@mui/icons-material/Theaters";
import ImageIcon from "@mui/icons-material/Image";
import LanguageIcon from "@mui/icons-material/Language";
import PreviewPlayer from "../previewPlayer/PreviewPlayer";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import UploadFile from "@mui/icons-material/UploadFile";
import clsx from "clsx";
import {
  updateMyDesignPlayerMode,
  updatePlaylistActivation,
  addOrRemoveElementInPlaylist,
} from "../../reducers/myDesigns/myDesignsThunks";
import GoldenSwitch from "../goldenSwitch/GoldenSwitch";

const PREFIX = "zoneContentManager";

const classes = {
  rootContainer: `${PREFIX}-rootContainer`,
  playlistsContainer: `${PREFIX}-playlistsContainer`,
  topTitleContainer: `${PREFIX}-topTitleContainer`,
  playlistContainer: `${PREFIX}-playlistContainer`,
  loaderContainer: `${PREFIX}-loaderContainer`,
  elementContainer: `${PREFIX}-elementContainer`,
  playlistInformations: `${PREFIX}-playlistInformations`,
  icons: `${PREFIX}-icons`,
  marginTop: `${PREFIX}-marginTop`,
  togglePosition: `${PREFIX}-togglePosition`,
  toggleVisuPosition: `${PREFIX}-toggleVisuPosition`,
  contentInfosContainer: `${PREFIX}-contentInfosContainer`,
  description: `${PREFIX}-description`,
  wordBreakStyle: `${PREFIX}-wordBreakStyle`,
  sectionTitle: `${PREFIX}-sectionTitle`,
  typeIcon: `${PREFIX}-typeIcon`,
  playPlaylistIcon: `${PREFIX}-playPlaylistIcon`,
  iconButtonPlayPlaylist: `${PREFIX}-iconButtonPlayPlaylist`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.rootContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "50%",
    position: "relative",
    margin: "0 auto",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
    },
  },

  [`& .${classes.playlistsContainer}`]: {
    marginTop: "1rem",
    [theme.breakpoints.up("sm")]: {
      margin: "0 auto",
      marginTop: "1rem",
    },
  },

  [`& .${classes.topTitleContainer}`]: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    maxWidth: "360px",
    margin: "0 auto",
    marginBottom: "1rem",
  },

  [`& .${classes.playlistContainer}`]: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      minWidth: "360px",
      // padding: "1rem",
    },
    height: "430px",
    [theme.breakpoints.between("xs", "md")]: {
      height: "400px",
      padding: "0.5rem",
    },
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    marginTop: "0.5rem",
    padding: "1px",
  },

  [`& .${classes.loaderContainer}`]: {
    position: "relative",
    left: "50%",
    transform: "translateX(-50%)",
    width: "20%",
    marginTop: "2rem",
  },

  [`& .${classes.elementContainer}`]: {
    height: "100px",
    maxWidth: "360px",
    marginBottom: "1rem",
    display: "flex",
    padding: "0 1rem",
    position: "relative",
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgb(66, 66, 66)",
  },

  [`& .${classes.playlistInformations}`]: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    margin: "0.5rem 0 1.5rem 0",
    width: "100%",
    height: "80%",
  },

  [`& .${classes.icons}`]: {
    width: "25px",
    height: "25px",
    color: theme.palette.primary.main,
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "0.5rem",
  },

  [`& .${classes.togglePosition}`]: {
    position: "absolute",
    right: "0",
  },

  [`& .${classes.toggleVisuPosition}`]: {
    position: "absolute",
    right: 0,
    bottom: 0,
    zIndex: 999,
  },

  [`& .${classes.contentInfosContainer}`]: {
    display: "flex",
    flexDirection: "row",
    paddingTop: "0.25rem",
    // height: '50%',
    width: "80%",
    position: "relative",
  },

  [`& .${classes.description}`]: {
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "90%",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },

  [`& .${classes.wordBreakStyle}`]: {
    width: "150px",
    marginLeft: "1rem !important",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    color: "#D3AF5F !important",
    fontSize: "1rem !important",
  },

  [`& .${classes.sectionTitle}`]: {
    textAlign: "center",
    color: "rgb(181, 154, 79)",
    marginTop: "0",
  },

  [`& .${classes.typeIcon}`]: {
    transform: "translateY(4px)",
  },

  [`& .${classes.playPlaylistIcon}`]: {
    width: "25px",
    height: "25px",
    color: theme.palette.primary.main,
  },

  [`& .${classes.iconButtonPlayPlaylist}`]: {
    height: "50px",
    width: "50px",
    margin: "0 auto",
  },
}));

export default function ZoneContentManager(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [togglePlaylist, setTogglePlaylist] = useState(
    props.currentZone.playlist === 1 ? true : false
  );
  const [togglePlayer, setTogglePlayer] = useState(props.currentZone.player);
  // const [open, setOpen] = useState(false);
  const [media, setMedia] = useState(null);
  const loading = useSelector((state) => state.myDesigns.selectedZone.loading);
  const changingMod = useSelector(
    (state) => state.myDesigns.selectedZone.changingMod
  );
  const content = useSelector((state) => state.myDesigns.selectedZone.content);
  const disableAction = useSelector(
    (state) => state.myDesigns.selectedZone.disableAction
  );

  const playlist = useSelector(
    (state) => state.myDesigns.selectedZone.playlist
  );

  const handleChange = async (event) => {
    try {
      const thunk = await dispatch(
        updatePlaylistActivation({
          zoneId: props.currentZone.id,
          value: !togglePlaylist,
        })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      setTogglePlaylist(!togglePlaylist);
      let tmp = { ...props.currentZone };
      const value = !togglePlaylist;
      tmp.playlist = !value ? 0 : 1;
      props.setCurrentZone(tmp);
    } catch {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  useEffect(() => {
    setTogglePlaylist(props.currentZone.playlist === 1 ? true : false);
  }, [props.currentZone]);

  const addOrRemoveMyElement = async (zoneId, elementId, activated, item) => {
    try {
      const thunk = await dispatch(
        addOrRemoveElementInPlaylist({ zoneId, elementId, activated, item })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
    } catch {
      enqueueSnackbar(t("toasts.getInfosError"), { variant: "error" });
    }
  };

  const MyContentElement = (props) => {
    if (props.item.playlist === 1 && props.inactive && !props.visualization) {
      return null;
    } else {
      return (
        <Paper
          elevation={24}
          className={classes.elementContainer}
          key={`element-playlist-${props.index}`}
        >
          <div className={classes.playlistInformations}>
            <div className={classes.contentInfosContainer}>
              {props.item.type === "video" ? (
                <TheatersIcon className={classes.typeIcon} />
              ) : props.item.type === "image" ? (
                <ImageIcon className={classes.typeIcon} />
              ) : (
                <LanguageIcon className={classes.typeIcon} />
              )}
              <Typography variant="h5" className={classes.wordBreakStyle}>
                {props.item.title ? props.item.title : props.currentZone.label}
              </Typography>
            </div>
            <IconButton
              onClick={() => {
                setMedia({
                  content: props.item.targetUrl
                    ? props.item.targetUrl
                    : props.item.directUrl,
                  title: props.item.title,
                  type: props.item.type,
                  description: props.item.description,
                });
              }}
              className={classes.toggleVisuPosition}
              color="primary"
              aria-label="upload picture"
              component="span"
              style={{
                padding: 0,
              }}
              size="large"
            >
              <VisibilityIcon />
            </IconButton>
            {props.playlist && (
              <IconButton
                onClick={() => {
                  addOrRemoveMyElement(
                    props.currentZone.id,
                    props.item.id,
                    false,
                    props.item
                  );
                }}
                disabled={disableAction}
                className={classes.togglePosition}
                color="primary"
                aria-label="upload picture"
                component="span"
                style={{
                  padding: 0,
                }}
                size="large"
              >
                <ClearIcon />
              </IconButton>
            )}
            {props.inactive && !props.visualization && (
              <IconButton
                onClick={() => {
                  addOrRemoveMyElement(
                    props.currentZone.id,
                    props.item.id,
                    true,
                    props.item
                  );
                }}
                disabled={disableAction}
                className={classes.togglePosition}
                color="primary"
                aria-label="upload picture"
                component="span"
                style={{
                  padding: 0,
                }}
                size="large"
              >
                <AddIcon />
              </IconButton>
            )}
            {props.item.description && (
              <Typography variant="body1" className={classes.description}>
                {props.item.description}
              </Typography>
            )}
          </div>
        </Paper>
      );
    }
  };

  const handleChangePlayer = async (name) => {
    if (props.currentZone.player !== name) {
      try {
        const thunk = await dispatch(
          updateMyDesignPlayerMode({
            zoneId: props.currentZone.id,
            value: name,
          })
        );
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
        setTogglePlayer(name);
        let tmp = { ...props.currentZone };
        tmp.player = name;
        props.setCurrentZone(tmp);
        enqueueSnackbar(t("toasts.updateMyDesignPlayerSuccess"), {
          variant: "success",
        });
      } catch {
        enqueueSnackbar(t("toasts.updateMyDesignPlayerError"), {
          variant: "error",
        });
      }
    }
  };

  if ((loading && !content) || !content || !playlist) {
    return (
      <Container>
        <Root>
          <div className={classes.loaderContainer}>
            <CircularProgress size={50} />
          </div>
        </Root>
      </Container>
    );
  } else {
    // const modalTitle =
    //   t("myDesign.zones.playlists.modalTitle") + props.currentZone.label;
    return (
      <Root>
        <div className={classes.rootContainer}>
          {/* <ResponsiveDialogManager
            title={modalTitle}
            open={open}
            onClose={() => {
              setOpen(false);
            }}
            renderer={
              <PlaylistContentManager
                playlist={playlist}
                zoneId={props.currentZone.id}
                zoneLabel={props.currentZone.label}
              />
            }
            toolbarActions={
              <Container>
                <Button
                  className={classes.buttonSpaces}
                  onClick={() => setOpen(false)}
                >
                  {t("myDesign.zones.playlists.finishButtonModal")}
                </Button>
              </Container>
            }
          /> */}
          <ResponsiveDialogManager
            title={media && media.title}
            fullScreen
            open={media ? true : false}
            onClose={() => {
              setMedia(null);
            }}
            renderer={<PreviewPlayer media={media && media} />}
          />
          <div className={classes.playlistsContainer}>
            {props.visualization && (
              <h1 className={classes.sectionTitle}>
                {t("myDesign.zones.playlists.myContent")}
              </h1>
            )}
            {props.orderPlaylist && (
              <>
                <div className={classes.topTitleContainer}>
                  <GoldenSwitch
                    checked={togglePlaylist}
                    onChange={handleChange}
                    name="playlist"
                    disabled={changingMod}
                    label={t("myDesign.zones.playlists.enablePlaylistMod")}
                  />
                  <IconButton
                    onClick={() => {
                      window.open(
                        `${process.env.REACT_APP_API_URL}/r/${props.currentZone.uniqueKey}/test`
                      );
                    }}
                    color="primary"
                    aria-label="launch playlist"
                    component="span"
                    className={classes.iconButtonPlayPlaylist}
                    size="large"
                  >
                    <PlayCircleOutlineIcon
                      className={clsx(classes.playPlaylistIcon, classes.icons)}
                    />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    aria-label="notify"
                    edge="end"
                    onClick={() => props.sendNotification()}
                    className={classes.iconButtonPlayPlaylist}
                    size="large"
                  >
                    <SettingsInputAntennaIcon className={classes.icons} />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    aria-label="modify"
                    edge="end"
                    onClick={() => props.upload()}
                    className={classes.iconButtonPlayPlaylist}
                    size="large"
                  >
                    <UploadFile className={classes.icons} />
                  </IconButton>
                  {/* <IconButton
                    className={classes.iconButtonPlayPlaylist}
                    color="primary"
                    aria-label="manage playlist"
                    component="span"
                    onClick={() => setOpen(true)}
                    size="large"
                  >
                    <CreateIcon className={classes.icons} />
                  </IconButton> */}
                </div>
                {togglePlaylist && (
                  <Box sx={{ display: "flex", textAlign: "center" }}>
                    <GoldenSwitch
                      checked={togglePlayer === "tiktok"}
                      onChange={() => handleChangePlayer("tiktok")}
                      name="tiktokPlayer"
                      disabled={changingMod}
                      label={t("myDesign.zones.playlists.playerTiktok")}
                    />
                    <GoldenSwitch
                      checked={togglePlayer === "insta"}
                      onChange={() => handleChangePlayer("insta")}
                      name="instaPlayer"
                      disabled={changingMod}
                      label={t("myDesign.zones.playlists.playerInsta")}
                    />
                  </Box>
                )}
              </>
            )}
            <Paper
              elevation={12}
              className={classes.playlistContainer}
              sx={{
                height: !props.visualization && props.orderPlaylist && "480px",
                padding:
                  props.visualization ||
                  (!props.visualization && !props.orderPlaylist)
                    ? "0.5rem !important"
                    : 0,
              }}
            >
              {content.length === 0 ? (
                <Typography
                  variant="body1"
                  className={classes.marginTop}
                  sx={{ textAlign: "center" }}
                >
                  {t("myDesign.zones.playlists.noResults")}
                </Typography>
              ) : props.visualization ||
                (!props.visualization && !props.orderPlaylist) ? (
                content.map((item, indexElem) => (
                  <MyContentElement
                    key={`content-${indexElem + 1}`}
                    item={item}
                    currentZone={props.currentZone}
                    inactive={!props.orderPlaylist}
                    visualization={props.visualization}
                  />
                ))
              ) : (
                // playlist.map((item, indexElem) => (
                //   <MyContentElement
                //     key={`content-${indexElem + 1}`}
                //     item={item}
                //     currentZone={props.currentZone}
                //     playlist={props.orderPlaylist}
                //   />
                // ))
                <PlaylistContentManager
                  playlist={playlist}
                  zoneId={props.currentZone.id}
                  zoneLabel={props.currentZone.label}
                  currentZone={props.currentZone}
                  setMedia={setMedia}
                  addOrRemoveMyElement={addOrRemoveMyElement}
                />
              )}
            </Paper>
          </div>
        </div>
      </Root>
    );
  }
}
